import { useCallback, useContext, useMemo, useState } from 'react';
import { ReactComponent as AttachIcon } from '@/assets/attach.svg';
import { AttachList } from './AttachList';
import classNames from 'classnames';
import { IFileResource, isAttachUiVisible } from '@enuan/chat__utils';
import { attachMessages } from './i18n';
import { bytesToSI, uglyBytes } from '@avalenti89/typescript-utils';
import { configurationContext } from '../../../contexts/configuration';
import { submitContext } from '../../../contexts/submit.context';
import { useAttachMedia } from './hooks';
import { useDidUpdateEffect } from '@avalenti89/react-ts-utils';
import { useIntl } from 'react-intl';
import { Modal } from '../../UI/Modal/Modal';
import { styled } from '@mui/material/styles';

type AttachProps = {
	disabled?: boolean;
	className?: string;
};

/**
 * Attach button that opens a modal picker allowing the user to select a file
 * (image, photo, or document) and attach it to the pending message.
 *
 * Delegates all validation and state updates to {@link useAttachMedia} so the
 * same rules apply whether a file is chosen here or pasted via keyboard.
 *
 * @param disabled - When true the button is rendered as disabled and cannot be
 *   clicked.
 */
export const Attach = styled(
	({ disabled, className }: AttachProps) => {
		const { message, setAction } = useContext(submitContext);
		const { file_upload, attach_ui } = useContext(configurationContext);

		const [isOpen, setOpen] = useState<boolean>(false);

		const { attachResource } = useAttachMedia();

		useDidUpdateEffect(() => {
			if (isOpen) {
				setAction('attaching');
			}
			if (!message && !isOpen) {
				setAction(undefined);
			}
		}, [isOpen, setAction, message]);

		/**
		 * Handles a resource selected from the modal.
		 * Passes `() => setOpen(false)` as the `onAttached` callback so the modal
		 * closes automatically on a valid selection.
		 */
		const onMedia = useCallback(
			(resource: IFileResource | null) => {
				attachResource(resource, () => setOpen(false));
			},
			[attachResource],
		);

		if (!file_upload?.enabled || !isAttachUiVisible({ attach_ui })) return null;

		return (
			<div className={classNames('form__attach', className)}>
				<button
					type="button"
					className={classNames(
						'attach__button',
						'form__action',
						disabled && 'form__action--disabled',
						isOpen && 'attach__button--open',
					)}
					disabled={disabled}
					onClick={() => {
						setOpen(true);
					}}
					unselectable="on"
					onContextMenu={(e) => {
						e.preventDefault();
						e.stopPropagation();
						return false;
					}}
				>
					<AttachIcon className="icon attach" />
				</button>
				<AttachModal
					isOpen={isOpen}
					onClose={() => setOpen(false)}
					onMedia={onMedia}
				/>
			</div>
		);
	},
	{
		target: 'Attach-root',
		label: 'Attach',
	},
)({
	width: '20px',
	position: 'relative',
	'& .attach__button': {
		position: 'absolute',
		marginTop: '-20px',
		marginLeft: '-10px',
		backgroundColor: 'var(--chat__footer__form__attach_color)',
		width: '40px',
		height: '40px',
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		'& .st0': {
			stroke: 'none',
		},

		'&.attach__button--open, &.attach__button:hover': {
			'&:not(.form__action--disabled), &:not(:disabled)': {
				transformOrigin: '50% 50%',

				'& .st0': {
					fill: 'var(--chat__footer__form__attach--hover_color)',
				},
			},
		},
		'&.attach__button--open': {
			transform: 'scale(1.25)',
		},
	},
});

// ── AttachModal ──────────────────────────────────────────────────────

type AttachModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onMedia: (resource: IFileResource | null) => void;
};

/**
 * Modal dialog that wraps {@link AttachList} with a title and a max-size
 * hint in the footer actions area.
 */
const AttachModal = ({ isOpen, onClose, onMedia }: AttachModalProps) => {
	const { file_upload: { max_size } = {} } = useContext(configurationContext);

	const uglySize = useMemo(() => uglyBytes(max_size ?? ''), [max_size]);
	const size = useMemo(() => bytesToSI(uglySize), [uglySize]);

	const intl = useIntl();
	const title = intl.formatMessage(attachMessages.title);
	const max_size_message = intl.formatMessage(attachMessages.max_size, {
		size,
	});

	return (
		<Modal
			open={isOpen}
			onClose={onClose}
			title={title}
			actions={<small>{max_size_message}</small>}
		>
			<AttachList onMedia={onMedia} />
		</Modal>
	);
};
